import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { normalizeTimezone } from 'utils/normalizeTimezone';
import { isLikelyPhoneNumber } from 'utils/phoneAndDateParser';
import { getSuggestionFormattedLabel } from 'utils/suggestions/suggestionFormat';
import {
  formatLoadDateTimeForDisplay,
  getStopTimezoneForFlattenedField,
} from 'utils/time';

import {
  getSectionNameForPipeline,
  stripPreviewSectionLabelPrefix,
} from './suggestionPreviewConfig';

dayjs.extend(utc);
dayjs.extend(timezone);

const DATETIME_DISPLAY_FORMAT = 'YYYY-MM-DD HH:mm';

// Suggestion fields that hold timestamps, keyed by the last path segment
// (pickup.readyTime -> readyTime). Other fields, like ref and PO numbers, are
// shown as-is even when dayjs can parse them (DL-0929-03 parses as year 929).
const DATETIME_FIELD_KEYS = new Set([
  // Load building stops
  'readyTime',
  'mustDeliver',
  'apptStartTime',
  'apptEndTime',
  // Carrier info and appointment confirmation
  'pickupApptTime',
  'dropoffApptTime',
  'dispatchedTime',
  'expectedPickupTime',
  // Check calls
  'timestamp',
  // Quick quote
  'pickupDate',
  'deliveryDate',
]);

function isDateTimeField(name: string): boolean {
  return DATETIME_FIELD_KEYS.has(name.split('.').pop() ?? name);
}

export function formatFieldValue(
  name: string,
  val: unknown,
  flattenedChanges: Record<string, unknown>,
  tmsName?: TMS
): string {
  const isDateLike =
    isDateTimeField(name) &&
    val &&
    (val instanceof Date ||
      (typeof val === 'string' &&
        val.length > 8 &&
        !isLikelyPhoneNumber(val))) &&
    dayjs(val).isValid();

  if (isDateLike && tmsName) {
    return formatLoadDateTimeForDisplay(val, tmsName, name, flattenedChanges);
  }

  if (isDateLike) {
    // Flat appt fields: pickupApptTime -> pickupApptTimezone
    if (name.endsWith('Time') && !name.includes('.')) {
      const timezoneValue = flattenedChanges[`${name}zone`];
      if (typeof timezoneValue === 'string' && timezoneValue) {
        const normalizedTimezone = normalizeTimezone(timezoneValue);
        if (normalizedTimezone) {
          return dayjs(val)
            .tz(normalizedTimezone)
            .format(DATETIME_DISPLAY_FORMAT);
        }
        return dayjs.utc(val).format(DATETIME_DISPLAY_FORMAT);
      }
    }

    // Nested stop fields: pickup.apptStartTime -> pickup.timezone
    if (name.includes('.')) {
      const stopTimezone = getStopTimezoneForFlattenedField(
        name,
        flattenedChanges
      );
      const normalizedTimezone = stopTimezone
        ? normalizeTimezone(stopTimezone)
        : undefined;
      if (normalizedTimezone) {
        return dayjs(val)
          .tz(normalizedTimezone)
          .format(DATETIME_DISPLAY_FORMAT);
      }
    }

    // No paired timezone: preserve wall clock (strip T/Z)
    if (typeof val === 'string') {
      return val.replace('T', ' ').replace('Z', '').slice(0, 16);
    }

    return dayjs.utc(val).format(DATETIME_DISPLAY_FORMAT);
  }

  return String(val ?? '');
}

export function groupAndMergeFields(
  fields: Array<[string, unknown]>,
  pipeline: SuggestionPipelines,
  flattenedChanges: Record<string, unknown>,
  tmsName?: TMS
): {
  mergedSections: Record<string, Array<{ label: string; value: string }>>;
  ungrouped: Array<{ label: string; value: string; originalName: string }>;
} {
  const sections: Record<
    string,
    Array<{ label: string; value: string; originalName: string }>
  > = {};
  const ungrouped: Array<{
    label: string;
    value: string;
    originalName: string;
  }> = [];

  fields.forEach(([name, val]) => {
    const changeLabel = getSuggestionFormattedLabel(pipeline, name);
    if (!changeLabel || !val) {
      return;
    }

    const formattedValue = formatFieldValue(
      name,
      val,
      flattenedChanges,
      tmsName
    );
    const sectionName = getSectionNameForPipeline(name, pipeline);

    if (sectionName) {
      if (!sections[sectionName]) {
        sections[sectionName] = [];
      }
      sections[sectionName].push({
        label: changeLabel,
        value: formattedValue,
        originalName: name,
      });
    } else {
      ungrouped.push({
        label: changeLabel,
        value: formattedValue,
        originalName: name,
      });
    }
  });

  const mergedSections: Record<
    string,
    Array<{ label: string; value: string }>
  > = {};

  Object.entries(sections).forEach(([sectionName, sectionFields]) => {
    const merged: Array<{ label: string; value: string }> = [];
    const processed = new Set<string>();

    const locationFields = ['city', 'state', 'zip', 'zipCode'];
    const locationGroups: Record<
      string,
      { city?: string; state?: string; zip?: string }
    > = {};

    sectionFields.forEach((field) => {
      const name = field.originalName.toLowerCase();
      const locationKey = sectionName;

      const isLocationField = locationFields.some((loc) => {
        return (
          name.includes(loc) ||
          name.endsWith(`.${loc}`) ||
          name.includes(`${loc}code`) ||
          name === loc
        );
      });

      if (isLocationField) {
        if (!locationGroups[locationKey]) {
          locationGroups[locationKey] = {};
        }
        if (name.includes('city') && !name.includes('dispatch')) {
          locationGroups[locationKey].city = field.value;
        } else if (name.includes('state') && !name.includes('dispatch')) {
          locationGroups[locationKey].state = field.value;
        } else if (name.includes('zip')) {
          locationGroups[locationKey].zip = field.value;
        }
        processed.add(field.originalName);
      }
    });

    Object.entries(locationGroups).forEach(([_, loc]) => {
      if (loc.city || loc.state || loc.zip) {
        const parts: string[] = [];
        if (loc.city) {
          parts.push(loc.city);
        }
        if (loc.state) {
          parts.push(loc.state);
        }
        if (loc.zip) {
          parts.push(loc.zip);
        }
        merged.push({
          label: 'Location',
          value: parts.join(', '),
        });
      }
    });

    sectionFields.forEach((field) => {
      if (!processed.has(field.originalName)) {
        merged.push({
          label: stripPreviewSectionLabelPrefix(field.label, sectionName),
          value: field.value,
        });
      }
    });

    mergedSections[sectionName] = merged;
  });

  return { mergedSections, ungrouped };
}
